import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api, type BodyType, type KV, type RunResult, type TestResult, type User } from "../lib/api";
import Spinner, { LoadingButton, InlineLoading } from "./Spinner";
import MethodPicker from "./MethodPicker";
import JsonCodeEditor from "./JsonCodeEditor";
import CodeViewer, { detectLangFromContentType, tryPrettyJson, type SyntaxLang } from "./CodeViewer";
import { useConfirm } from "./ConfirmDialog";
import Markdown from "./Markdown";
import Comments from "./Comments";
import Avatar from "./Avatar";
import { emit as emitCurl, type RequestSpec as CurlSpec } from "@rest/curl";
import CodeExport from "./CodeExport";
import type { RequestSpec as CodegenSpec } from "@rest/codegen";
import { EnvDot } from "../pages/Environments";
import ChipInput from "./ChipInput";
import RunDetailModal from "./RunDetailModal";
import { findVars, interpolate } from "../lib/envInterp";
import { readExecutor, readAgentId, readEnvId } from "./Topbar";
import { useResizable, useMediaQuery } from "../lib/useResizable";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

type Tab = "params" | "headers" | "body" | "docs" | "comments" | "history";

interface RunExtras { tests?: TestResult[]; scriptLog?: unknown[] }

export default function RequestBuilder({ requestId, workspaceId }: { requestId: string; workspaceId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const detailQ = useQuery({
    queryKey: ["request", requestId],
    queryFn: () => api.requests.get(requestId),
  });
  const data = detailQ.data?.request ?? null;

  // Agents + environments + me for the run/comments features.
  const agentsQ = useQuery({
    queryKey: ["agents", workspaceId],
    queryFn: () => api.agents.list(workspaceId),
  });
  const envsQ = useQuery({
    queryKey: ["environments", workspaceId],
    queryFn: () => api.environments.list(workspaceId),
  });
  const meQ = useQuery({ queryKey: ["me"], queryFn: () => api.me() });
  const me: User | null = meQ.data?.user ?? null;

  // Local editor state — initialized from the loaded request, then editable.
  const [name, setName] = useState("");
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("");
  const [headers, setHeaders] = useState<KV[]>([]);
  const [query, setQuery] = useState<KV[]>([]);
  const [bodyType, setBodyType] = useState<BodyType>("none");
  const [body, setBody] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [docs, setDocs] = useState<string>("");
  const [preScript, setPreScript] = useState<string>("");
  const [postScript, setPostScript] = useState<string>("");
  const [testsScript, setTestsScript] = useState<string>("");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // Tab + env are URL-driven so they're deep-linkable. Falling back to the
  // first time we render with no query param lands on the default tab.
  const [sp, setSp] = useSearchParams();
  const tab: Tab = ((): Tab => {
    const q = sp.get("tab") ?? "";
    const valid: Tab[] = ["params", "headers", "body", "docs", "comments", "history"];
    return (valid as string[]).includes(q) ? (q as Tab) : "params";
  })();
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(sp);
    if (t === "params") next.delete("tab"); else next.set("tab", t);
    setSp(next, { replace: true });
  };
  const [response, setResponse] = useState<(RunResult & RunExtras) | null>(null);
  // Executor + agent live in localStorage so they're workspace-wide, set
  // from the Top bar. Env is URL-driven (?env=…).
  const executor = readExecutor(workspaceId);
  const agentId = readAgentId(workspaceId);
  const envId = sp.get("env") || readEnvId(workspaceId);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [codeOpen, setCodeOpen] = useState(false);

  // Reset local state whenever a different request loads.
  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setMethod(data.method);
    setUrl(data.url);
    setHeaders(data.headers.length ? data.headers : []);
    setQuery(data.query.length ? data.query : []);
    setBodyType(data.body_type as BodyType);
    setBody(data.body ?? "");
    setDescription(data.description ?? "");
    setDocs(data.docs ?? "");
    setPreScript(data.pre_request_script ?? "");
    setPostScript(data.post_response_script ?? "");
    setTestsScript(data.tests_script ?? "");
    setSavedAt(data.updated_at);
    setResponse(null);
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = useMemo(() => {
    if (!data) return false;
    return (
      data.name !== name ||
      data.method !== method ||
      data.url !== url ||
      JSON.stringify(data.headers) !== JSON.stringify(headers) ||
      JSON.stringify(data.query) !== JSON.stringify(query) ||
      data.body_type !== bodyType ||
      (data.body ?? "") !== body ||
      (data.description ?? "") !== description ||
      (data.docs ?? "") !== docs ||
      (data.pre_request_script ?? "") !== preScript ||
      (data.post_response_script ?? "") !== postScript ||
      (data.tests_script ?? "") !== testsScript
    );
  }, [data, name, method, url, headers, query, bodyType, body, description, docs, preScript, postScript, testsScript]);

  const save = useMutation({
    mutationFn: (note?: string) => api.requests.update(requestId, {
      name, method, url, headers, query, body_type: bodyType, body, description,
      docs, pre_request_script: preScript || null, post_response_script: postScript || null, tests_script: testsScript || null,
      note,
    }),
    onSuccess: () => {
      setSavedAt(new Date().toISOString());
      qc.invalidateQueries({ queryKey: ["request", requestId] });
      qc.invalidateQueries({ queryKey: ["revisions", requestId] });
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["requests", data.collection_id] });
    },
  });

  const run = useMutation({
    mutationFn: async () => {
      // Save first if dirty so the agent runs the current scripts.
      if (isDirty) await save.mutateAsync(undefined);
      // Hold the loading state for at least 300ms so the user gets a
      // visible "sending" beat even on fast local responses. Real-world
      // requests usually take longer so this is rarely the bottleneck.
      const started = Date.now();
      const result = await api.requests.run(requestId, {
        overrides: { method, url, headers, query, body_type: bodyType, body },
        environmentId: envId || undefined,
        executor,
        agentId: executor === "agent" ? (agentId || undefined) : undefined,
      });
      const elapsed = Date.now() - started;
      if (elapsed < 300) await new Promise((r) => setTimeout(r, 300 - elapsed));
      return result;
    },
    onSuccess: (r) => {
      setResponse(r);
      qc.invalidateQueries({ queryKey: ["runs", requestId] });
      if (isDirty) {
        setSavedAt(new Date().toISOString());
        qc.invalidateQueries({ queryKey: ["request", requestId] });
        qc.invalidateQueries({ queryKey: ["revisions", requestId] });
        if (data?.collection_id) qc.invalidateQueries({ queryKey: ["requests", data.collection_id] });
      }
    },
  });

  const onlineAgents = (agentsQ.data?.agents ?? []).filter((a) => a.online);

  const remove = useMutation({
    mutationFn: () => api.requests.remove(requestId),
    onSuccess: () => {
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["requests", data.collection_id] });
      window.location.assign("/");
    },
  });

  // Resizable split between the editor and the response panel. Declared
  // BEFORE the early returns below — hooks must run on every render.
  const isDesktopSplit = useMediaQuery("(min-width: 768px)");
  const { size: editorWidth, dragProps: editorDragX } = useResizable({
    axis: "x", initial: 540, min: 100, max: 1600, storageKey: "rest.builder.editorWidth",
  });
  const { size: editorHeight, dragProps: editorDragY } = useResizable({
    axis: "y", initial: 320, min: 100, max: 1200, storageKey: "rest.builder.editorHeight",
  });
  const editorPaneStyle = isDesktopSplit
    ? { width: editorWidth }
    : { height: editorHeight };

  // Stable resolver + suggestion source for ChipInput — declared BEFORE
  // the early returns so hook order is consistent across renders.
  //
  // `allVars` is the UNION of variable names across every environment
  // in the workspace. We use this both for the autosuggest list (so
  // typing `H` shows HOST even when no env is selected) and the
  // "is this resolved" check (so the chip stays green if the name is
  // defined in *any* env, even when a different env is active).
  // The active env's value wins for the hint shown in the popover.
  const activeEnv = (envsQ.data?.environments ?? []).find((e) => e.id === envId);
  const allEnvKeySig = (envsQ.data?.environments ?? [])
    .flatMap((e) => Object.keys(e.vars ?? {}))
    .sort()
    .join("|") + "|active:" + (envId ?? "");
  const allVars = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const env of envsQ.data?.environments ?? []) {
      for (const [k, v] of Object.entries(env.vars ?? {})) {
        if (!(k in out)) out[k] = v;
      }
    }
    // Active env's value wins (if present) so the popover hint matches
    // what would actually substitute on Send.
    if (activeEnv) {
      for (const [k, v] of Object.entries(activeEnv.vars ?? {})) out[k] = v;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEnvKeySig]);
  // A chip is "resolved" strictly relative to the active env. If no
  // env is selected, NOTHING resolves — every chip is red until you
  // pick one. Otherwise it's resolved only if the active env defines
  // that name. Switch envs → chips flip color in place.
  const activeEnvKeySig = Object.keys(activeEnv?.vars ?? {}).sort().join("|");
  const isVarResolved = useMemo(() => {
    if (!activeEnv) return () => false;
    const keys = new Set(Object.keys(activeEnv.vars ?? {}));
    return (n: string) => keys.has(n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnv?.id, activeEnvKeySig]);
  // Tooltip value lookup — returns the value of a variable in the
  // active env (or undefined if not present). The chip uses this for
  // its `title` attr so hovering shows the substitution.
  const resolveVarValue = useMemo(() => {
    if (!activeEnv) return () => undefined;
    return (n: string) => activeEnv.vars?.[n];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnv?.id, activeEnvKeySig]);

  if (detailQ.isLoading) {
    return <div className="grid h-full place-items-center"><InlineLoading label="Loading request…" /></div>;
  }
  if (!data) {
    return <div className="grid h-full place-items-center muted">Request not found.</div>;
  }

  const sendDisabled = !url || run.isPending;
  // One-line explanation surfaced as a tooltip when the user hovers
  // the disabled Send button. Order matters — pending is the more
  // useful reason to show when both conditions apply.
  const sendDisabledReason: string | null =
    run.isPending ? "A request is already in flight — wait for it to finish."
    : !url ? "Enter a URL above before you can send."
    : null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="border-b border-border bg-surface px-3 py-1.5 flex items-center gap-2">
        {/* Name lives in the sidebar + tab strip now — double-click either to
            rename. The static label keeps the header from feeling empty. */}
        <div className="flex-1 min-w-0 truncate text-sm font-medium text-text" title={name}>{name || "Untitled request"}</div>
        {isDirty && <span className="chip bg-warn/15 text-warn">Unsaved</span>}
        {!isDirty && savedAt && (
          <span className="text-xs muted-strong">Saved {timeAgo(savedAt)}</span>
        )}
        {copyToast && <span className="chip bg-good/15 text-good">{copyToast}</span>}
        <LoadingButton
          className="btn"
          pending={save.isPending}
          disabled={!isDirty}
          onClick={() => save.mutate(undefined)}
        >
          Save
        </LoadingButton>
        <RequestMenu
          onDelete={async () => {
            const ok = await confirm({
              title: `Delete "${data.name}"?`,
              description: "This permanently removes the request and all its revisions and runs.",
              danger: true,
            });
            if (ok) remove.mutate();
          }}
        />
      </div>
      {codeOpen && (
        <CodeExport
          spec={{
            name,
            method: method as CodegenSpec["method"],
            // Substitute `{{var}}` references with the active env's
            // values so the exported snippet is runnable as-is.
            url: activeEnv ? interpolate(url, activeEnv.vars) : url,
            headers: headers.map((h) => ({
              key: activeEnv ? interpolate(h.key, activeEnv.vars) : h.key,
              value: activeEnv ? interpolate(h.value, activeEnv.vars) : h.value,
              enabled: h.enabled !== false,
            })),
            query: query.map((q) => ({
              key: activeEnv ? interpolate(q.key, activeEnv.vars) : q.key,
              value: activeEnv ? interpolate(q.value, activeEnv.vars) : q.value,
              enabled: q.enabled !== false,
            })),
            bodyType: bodyType as CodegenSpec["bodyType"],
            body: body ? (activeEnv ? interpolate(body, activeEnv.vars) : body) : null,
            auth: { kind: "none", fields: {} },
          }}
          onClose={() => setCodeOpen(false)}
        />
      )}

      <div className="px-3 py-3 flex flex-wrap sm:flex-nowrap items-stretch gap-2 border-b border-border">
        <MethodPicker value={method} onChange={setMethod} className="shrink-0" />
        <div className="min-w-0 relative flex items-center order-3 sm:order-none basis-full sm:basis-auto sm:flex-1">
          <ChipInput
            value={url}
            onChange={setUrl}
            suggestions={Object.keys(allVars).sort().map((n) => ({ name: n, hint: allVars[n] ?? "" }))}
            isResolved={isVarResolved}
            resolveValue={resolveVarValue}
            placeholder="Request URL — https://api.example.com/v1/users"
            spellCheck={false}
            aria-label="Request URL"
            className="!text-sm"
            onEnter={() => { if (!sendDisabled) run.mutate(); }}
          />
        </div>
        <SendButton
          className="ml-auto sm:ml-0"
          pending={run.isPending}
          disabled={sendDisabled}
          disabledReason={sendDisabledReason}
          onSend={() => run.mutate()}
          onCopyCurl={async () => {
            // Interpolate everything against the active env so the
            // copied curl is runnable as-is, not a template.
            const interp = (s: string) => activeEnv ? interpolate(s, activeEnv.vars) : s;
            const spec: CurlSpec = {
              name,
              method: method as CurlSpec["method"],
              url: interp(url),
              headers: headers.map((h) => ({ key: interp(h.key), value: interp(h.value), enabled: h.enabled !== false })),
              query:   query.map((q) => ({ key: interp(q.key), value: interp(q.value), enabled: q.enabled !== false })),
              bodyType: bodyType as CurlSpec["bodyType"],
              body: body ? interp(body) : null,
              auth: { kind: "none", fields: {} },
              followRedirects: true, insecure: false, unsupported: [],
            };
            const out = emitCurl(spec);
            try { await navigator.clipboard.writeText(out); setCopyToast("Copied curl"); }
            catch { setCopyToast("Copy failed"); }
            setTimeout(() => setCopyToast(null), 2_000);
          }}
          onShowCode={() => setCodeOpen(true)}
        />
      </div>

      <div className="border-b border-border bg-surface px-3 overflow-x-auto">
        <Tabs current={tab} onChange={setTab} counts={{
          params: query.filter((q) => q.key).length,
          headers: headers.filter((h) => h.key).length,
          docs: docs ? 1 : 0,
        }} />
      </div>

      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
        {/* The docs/comments/history tabs are full-width — there's
            nothing useful to look at in the response pane while you're
            reading docs or scrolling history. Drop the split and let
            the editor pane fill the bottom. */}
        {(() => {
          const fullWidth = tab === "docs" || tab === "comments" || tab === "history";
          return (
            <>
              <div
                className={
                  "min-h-0 overflow-auto scroll-pane p-3 border-b md:border-b-0 md:border-r border-border " +
                  (fullWidth ? "flex-1 min-w-0" : "shrink-0")
                }
                style={fullWidth ? undefined : editorPaneStyle}
              >
                {tab === "params" && (
                  <KVEditor
                    items={query}
                    onChange={setQuery}
                    keyLabel="Param"
                    valueLabel="Value"
                    vars={allVars}
                    envVarNames={Object.keys(allVars).sort()}
                    isResolved={isVarResolved}
                    resolveValue={resolveVarValue}
                  />
                )}
                {tab === "headers" && (
                  <KVEditor
                    items={headers}
                    onChange={setHeaders}
                    keyLabel="Header"
                    valueLabel="Value"
                    vars={allVars}
                    envVarNames={Object.keys(allVars).sort()}
                    isResolved={isVarResolved}
                    resolveValue={resolveVarValue}
                  />
                )}
                {tab === "body" && (
                  <BodyEditor
                    bodyType={bodyType}
                    body={body}
                    onTypeChange={setBodyType}
                    onBodyChange={setBody}
                  />
                )}
                {tab === "docs" && (
                  <DocsEditor description={description} setDescription={setDescription} docs={docs} setDocs={setDocs} />
                )}
                {tab === "comments" && me && (
                  <Comments targetKind="request" targetId={requestId} me={me} workspaceId={workspaceId} />
                )}
                {tab === "history" && (
                  <HistoryPane
                    requestId={requestId}
                    onRestore={() => {
                      qc.invalidateQueries({ queryKey: ["request", requestId] });
                      qc.invalidateQueries({ queryKey: ["revisions", requestId] });
                    }}
                  />
                )}
              </div>
              {!fullWidth && (
                <>
                  {/* Drag handles. Only mounted when the split is live;
                      docs/comments/history make the editor full-width and
                      drop both the handles and the response pane. */}
                  <div
                    {...editorDragX}
                    aria-label="Resize editor / response"
                    className="hidden md:block w-1.5 cursor-col-resize bg-transparent hover:bg-accent/30 active:bg-accent/50 transition-colors shrink-0"
                  />
                  <div
                    {...editorDragY}
                    aria-label="Resize editor / response"
                    className="block md:hidden h-1.5 cursor-row-resize bg-transparent hover:bg-accent/30 active:bg-accent/50 transition-colors shrink-0"
                  />
                  <div className="flex-1 min-w-[100px] min-h-[100px] overflow-auto scroll-pane">
                    <ResponseViewer
                      response={response}
                      error={run.error ? String((run.error as Error).message) : null}
                      pending={run.isPending}
                      requestId={requestId}
                      workspaceId={workspaceId}
                    />
                  </div>
                </>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

function Tabs({ current, onChange, counts }: { current: Tab; onChange: (t: Tab) => void; counts: { params: number; headers: number; docs: number } }) {
  const items: Array<{ id: Tab; label: string; count?: number }> = [
    { id: "params", label: "Params", count: counts.params },
    { id: "headers", label: "Headers", count: counts.headers },
    { id: "body", label: "Body" },
    { id: "docs", label: "Docs", count: counts.docs },
    { id: "comments", label: "Comments" },
    { id: "history", label: "History" },
  ];
  return (
    <div className="flex gap-1">
      {items.map((t) => {
        const active = t.id === current;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px " +
              (active ? "border-accent text-text" : "border-transparent text-text-dim hover:text-text")
            }
          >
            {t.label}
            {!!t.count && (
              <span className="ml-1.5 chip bg-surface-3 muted-strong">{t.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function KVEditor({
  items,
  onChange,
  keyLabel,
  valueLabel,
  vars,
  envVarNames,
  isResolved,
  resolveValue,
}: {
  items: KV[];
  onChange: (v: KV[]) => void;
  keyLabel: string;
  valueLabel: string;
  vars: Record<string, string>;
  envVarNames: string[];
  isResolved: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
}) {
  const list = items;
  // Stable row IDs so dnd-kit can identify rows across re-renders. We
  // mint one per row index lazily — the underlying KV[] has no id, so
  // we just use the row position.
  const ids = useMemo(() => list.map((_, i) => `kv-${i}`), [list]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const set = (i: number, patch: Partial<KV>) => {
    onChange(list.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };
  const removeRow = (i: number) => onChange(list.filter((_, idx) => idx !== i));
  const addRow = () => onChange([...list, { key: "", value: "", enabled: true }]);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onChange(arrayMove(list, from, to));
  };

  return (
    <div className="space-y-1">
      {list.length > 0 && (
        <div className="grid grid-cols-[18px_28px_1fr_1fr_28px] gap-2 px-2 text-[11px] uppercase tracking-wider muted-strong">
          <div></div>
          <div></div>
          <div>{keyLabel}</div>
          <div>{valueLabel}</div>
          <div></div>
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictKVtoVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {list.map((it, i) => (
            <KVRow
              key={ids[i]}
              id={ids[i]}
              kv={it}
              keyLabel={keyLabel}
              valueLabel={valueLabel}
              vars={vars}
              envVarNames={envVarNames}
              isResolved={isResolved}
              resolveValue={resolveValue}
              onPatch={(p) => set(i, p)}
              onRemove={() => removeRow(i)}
            />
          ))}
        </SortableContext>
      </DndContext>
      {list.length === 0 && (
        <div className="empty-card">No {valueLabel.toLowerCase()}s yet. Click <span className="empty-card-strong">+ Add</span> to add one.</div>
      )}
      <div className="pt-1">
        <button type="button" className="btn" onClick={addRow}>＋ Add {keyLabel.toLowerCase()}</button>
      </div>
    </div>
  );
}

function KVRow({
  id, kv, keyLabel, valueLabel, vars, envVarNames, isResolved, resolveValue, onPatch, onRemove,
}: {
  id: string;
  kv: KV;
  keyLabel: string;
  valueLabel: string;
  vars: Record<string, string>;
  envVarNames: string[];
  isResolved: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
  onPatch: (p: Partial<KV>) => void;
  onRemove: () => void;
}) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="grid grid-cols-[18px_28px_1fr_1fr_28px] gap-2 items-center">
      <button
        type="button"
        {...attributes}
        {...listeners}
        title="Drag to reorder"
        aria-label="Drag to reorder"
        className="h-7 w-[18px] grid place-items-center text-text-faint hover:text-text cursor-grab active:cursor-grabbing select-none"
      >
        <DragHandleIcon />
      </button>
      <input
        type="checkbox"
        checked={kv.enabled !== false}
        onChange={(e) => onPatch({ enabled: e.target.checked })}
        className="h-4 w-4 rounded justify-self-center"
      />
      <ChipInput
        value={kv.key}
        onChange={(v) => onPatch({ key: v })}
        suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
        isResolved={isResolved}
        resolveValue={resolveValue}
        placeholder={keyLabel}
        spellCheck={false}
      />
      <ChipInput
        value={kv.value}
        onChange={(v) => onPatch({ value: v })}
        suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
        isResolved={isResolved}
        resolveValue={resolveValue}
        placeholder={valueLabel}
        spellCheck={false}
      />
      <button
        type="button"
        onClick={onRemove}
        className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2 justify-self-center"
        title="Remove"
        aria-label="Remove"
      >
        ✕
      </button>
    </div>
  );
}

function DragHandleIcon() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" aria-hidden="true">
      <g fill="currentColor">
        <circle cx="2" cy="2" r="1.2" />
        <circle cx="8" cy="2" r="1.2" />
        <circle cx="2" cy="7" r="1.2" />
        <circle cx="8" cy="7" r="1.2" />
        <circle cx="2" cy="12" r="1.2" />
        <circle cx="8" cy="12" r="1.2" />
      </g>
    </svg>
  );
}

// dnd-kit modifier that constrains the dragged item to the vertical axis
// so the row can only shuffle up/down (no horizontal drift).
function restrictKVtoVerticalAxis({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) {
  return { ...transform, x: 0 };
}

function BodyEditor({
  bodyType,
  body,
  onTypeChange,
  onBodyChange,
}: {
  bodyType: BodyType;
  body: string;
  onTypeChange: (t: BodyType) => void;
  onBodyChange: (s: string) => void;
}) {
  const types: BodyType[] = ["none", "json", "text", "form"];

  // Validate JSON on each change; show a precise error pill when invalid.
  // The native JSON.parse error message includes the byte offset for V8
  // ("at position 42"), so we surface it as-is — useful for fixing typos.
  const jsonError = useMemo(() => {
    if (bodyType !== "json" || !body.trim()) return null;
    try { JSON.parse(body); return null; }
    catch (e: unknown) { return String((e as Error).message ?? e); }
  }, [body, bodyType]);

  return (
    <div className="space-y-2 h-full flex flex-col">
      <div className="flex gap-1 items-center">
        {types.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTypeChange(t)}
            className={
              "px-3 py-1 rounded-sm text-xs font-medium " +
              (t === bodyType ? "bg-accent text-[var(--ink-on-accent)]" : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
            }
          >
            {t}
          </button>
        ))}
      </div>
      {bodyType === "json" && jsonError && (
        <div className="rounded-md border border-danger/20 bg-danger/[0.06] px-2 py-1 text-[11px] text-danger/80 flex items-center gap-2">
          <span aria-hidden="true" className="opacity-70">⚠</span>
          <span className="flex-1 truncate" title={jsonError}>Invalid JSON · {jsonError}</span>
        </div>
      )}
      {bodyType === "none" ? (
        <div className="empty-card">
          No body sent with this request. Pick <span className="empty-card-strong">JSON</span>, <span className="empty-card-strong">text</span>, or <span className="empty-card-strong">form</span> above to add one.
        </div>
      ) : bodyType === "json" ? (
        <div className="relative flex-1 min-h-[160px]">
          <JsonCodeEditor value={body} onChange={onBodyChange} />
          {/* Format lives inside the editor, bottom-right, as a small
              pill that fades in on hover so it doesn't fight with the
              code for attention. */}
          <button
            type="button"
            disabled={!!jsonError || !body.trim()}
            onClick={() => {
              try { onBodyChange(JSON.stringify(JSON.parse(body), null, 2)); } catch { /* leave alone */ }
            }}
            className="absolute bottom-2 right-3 z-10 h-6 px-2 rounded bg-surface-2/90 border border-border text-[11px] font-medium text-text-dim hover:text-text hover:bg-surface-3 backdrop-blur-sm disabled:opacity-40 disabled:cursor-not-allowed transition"
          >Format</button>
        </div>
      ) : (
        <div className="relative flex-1 min-h-[160px]">
          <textarea
            className="input w-full h-full input-mono min-h-[160px]"
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            placeholder={bodyType === "form" ? "a=1&b=2" : "Raw body"}
            spellCheck={false}
          />
        </div>
      )}
    </div>
  );
}

function ScriptEditor({
  title, hint, code, onChange, example,
}: { title: string; hint: string; code: string; onChange: (s: string) => void; example: string }) {
  return (
    <div className="h-full flex flex-col space-y-2">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs muted mt-1">{hint}</p>
      </div>
      <textarea
        className="input flex-1 input-mono min-h-[200px]"
        value={code}
        onChange={(e) => onChange(e.target.value)}
        placeholder={example}
        spellCheck={false}
      />
      <p className="text-[11px] muted-strong">
        Scripts run on the agent. Connect one from <a href="/agents" className="accent-link">Agents</a> and switch the
        executor to "Local agent" above.
      </p>
    </div>
  );
}

function DocsEditor({ description, setDescription, docs, setDocs }: { description: string; setDescription: (s: string) => void; docs: string; setDocs: (s: string) => void }) {
  const [mode, setMode] = useState<"edit" | "preview" | "split">("split");
  return (
    <div className="space-y-3 h-full flex flex-col min-w-0">
      <div className="min-w-0">
        <label className="label">Short description</label>
        <input className="input mt-1" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One line — shows up in lists." />
      </div>
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <label className="label">Full docs (Markdown)</label>
        <div className="ml-auto flex gap-1">
          {(["edit", "split", "preview"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={
                "px-2 py-0.5 rounded-sm text-xs font-medium " +
                (m === mode ? "bg-accent text-[var(--ink-on-accent)]" : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
              }
            >{m}</button>
          ))}
        </div>
      </div>
      <div className={"flex-1 min-h-[240px] min-w-0 grid gap-3 " + (mode === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
        {mode !== "preview" && (
          <textarea
            className="input input-mono min-h-[240px] min-w-0 w-full"
            value={docs}
            onChange={(e) => setDocs(e.target.value)}
            placeholder={`## What this endpoint does\n\n- bullet\n- bullet\n\n### Response shape\n\n\`\`\`json\n{ "id": 123 }\n\`\`\``}
            spellCheck={false}
          />
        )}
        {mode !== "edit" && (
          <div className="rounded border border-border bg-surface-2 p-3 overflow-auto scroll-pane min-h-[240px] min-w-0">
            {docs ? <Markdown source={docs} /> : <p className="text-sm muted italic">Nothing to preview yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function HistoryPane({ requestId, onRestore }: { requestId: string; onRestore: () => void }) {
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const qc = useQueryClient();
  const revs = useQuery({
    queryKey: ["revisions", requestId],
    queryFn: () => api.requests.revisions(requestId),
  });
  const runs = useQuery({
    queryKey: ["runs", requestId],
    queryFn: () => api.requests.runs(requestId),
  });
  const restore = useMutation({
    mutationFn: (revId: string) => api.requests.restoreRevision(requestId, revId),
    onSuccess: () => {
      onRestore();
      qc.invalidateQueries({ queryKey: ["revisions", requestId] });
    },
  });

  const revisions = revs.data?.revisions ?? [];

  return (
    <div className="space-y-6 min-w-0">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold mb-2">Revisions</h3>
        <p className="text-xs muted mb-3">Every save creates a revision. Click one to see what changed from the version before; restore to recover that state — restoring also creates a new revision so nothing is lost.</p>
        <ul className="space-y-1">
          {revisions.map((r, idx) => {
            // Revisions are returned newest-first, so the "previous" version
            // is the next entry in the list.
            const prev = revisions[idx + 1] ?? null;
            const avatarSrc = r.author_id && r.author_avatar_updated_at
              ? api.avatars.userUrl(r.author_id, r.author_avatar_updated_at)
              : null;
            const isOpen = expanded === r.id;
            return (
              <li key={r.id} className="rounded border border-transparent hover:bg-surface-2 data-[open=true]:border-border data-[open=true]:bg-surface-2" data-open={isOpen}>
                <div className="flex items-center gap-2 sm:gap-3 px-2 py-1.5 min-w-0">
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : r.id)}
                    className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 text-left"
                    aria-expanded={isOpen}
                    title={isOpen ? "Hide diff" : "Show diff vs previous version"}
                  >
                    <span className="chip bg-surface-3 muted-strong mono shrink-0">#{r.revision_no}</span>
                    <span className="shrink-0"><Avatar src={avatarSrc} name={r.author_name} email={r.author_email ?? "unknown@unknown"} size={26} /></span>
                    <span className={"method-tag shrink-0 " + r.method.toLowerCase()}>{r.method}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{r.name}</div>
                      <div className="text-[11px] muted-strong truncate">
                        {r.note ?? "—"} · {r.author_name ?? r.author_email ?? "unknown"} · {new Date(r.created_at).toLocaleString()}
                      </div>
                    </div>
                  </button>
                  <LoadingButton
                    className="btn shrink-0"
                    pending={restore.isPending && restore.variables === r.id}
                    onClick={() => restore.mutate(r.id)}
                  >Restore</LoadingButton>
                </div>
                {isOpen && (
                  <div className="border-t border-border px-3 py-3">
                    {prev ? (
                      <RevisionDiff requestId={requestId} fromId={prev.id} toId={r.id} fromNo={prev.revision_no} toNo={r.revision_no} />
                    ) : (
                      <p className="text-xs muted italic">First revision — nothing to compare against.</p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {revs.data && revisions.length === 0 && (
            <li className="empty-card">No revisions yet. Hit <span className="empty-card-strong">Save</span> to capture the first one.</li>
          )}
        </ul>
      </div>

      <div className="min-w-0">
        <h3 className="text-sm font-semibold mb-2">Runs</h3>
        <p className="text-xs muted mb-3">Last 100 executions of this request.</p>
        <ul className="space-y-1">
          {(runs.data?.runs ?? []).map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setOpenRunId(r.id)}
                className="w-full flex items-center gap-2 sm:gap-3 px-2 py-1.5 rounded hover:bg-surface-2 text-left min-w-0"
                title="Open run details"
              >
                <span className="shrink-0"><StatusPill status={r.status} error={r.error} /></span>
                <span className={"method-tag shrink-0 " + r.method.toLowerCase()}>{r.method}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs truncate font-mono">{r.url}</div>
                  <div className="text-[11px] muted-strong truncate">
                    {new Date(r.ran_at).toLocaleString()} · {r.duration_ms} ms
                    {r.response_size != null && ` · ${formatBytes(r.response_size)}`}
                  </div>
                </div>
              </button>
            </li>
          ))}
          {runs.data && runs.data.runs.length === 0 && (
            <li className="empty-card">No runs yet. Press <span className="empty-card-strong">Send</span> to execute this request.</li>
          )}
        </ul>
      </div>
      <RunDetailModal requestId={requestId} runId={openRunId} onClose={() => setOpenRunId(null)} />
    </div>
  );
}

// Shows a per-field diff between two revisions: what changed in name,
// method, URL, headers, query, body_type, body, description. We fetch
// detail for both revisions and walk the fields. Headers + query are
// compared as ordered key/value lists.
function RevisionDiff({ requestId, fromId, toId, fromNo, toNo }: {
  requestId: string;
  fromId: string;
  toId: string;
  fromNo: number;
  toNo: number;
}) {
  const fromQ = useQuery({
    queryKey: ["revision", requestId, fromId],
    queryFn: () => api.requests.revision(requestId, fromId),
  });
  const toQ = useQuery({
    queryKey: ["revision", requestId, toId],
    queryFn: () => api.requests.revision(requestId, toId),
  });
  if (fromQ.isLoading || toQ.isLoading) {
    return <div className="text-xs muted"><InlineLoading label="Loading diff…" /></div>;
  }
  const a = fromQ.data?.revision;
  const b = toQ.data?.revision;
  if (!a || !b) {
    return <p className="text-xs muted italic">Couldn't load one or both revisions.</p>;
  }

  const scalarRows: { label: string; before: string; after: string }[] = [
    { label: "Name", before: a.name ?? "", after: b.name ?? "" },
    { label: "Method", before: a.method, after: b.method },
    { label: "URL", before: a.url ?? "", after: b.url ?? "" },
    { label: "Body type", before: a.body_type, after: b.body_type },
    { label: "Description", before: a.description ?? "", after: b.description ?? "" },
  ];
  const scalarChanges = scalarRows.filter((r) => r.before !== r.after);
  const headerChanges = diffKVList(a.headers ?? [], b.headers ?? []);
  const queryChanges = diffKVList(a.query ?? [], b.query ?? []);
  const bodyChanged = (a.body ?? "") !== (b.body ?? "");
  const anyChange =
    scalarChanges.length > 0 ||
    headerChanges.length > 0 ||
    queryChanges.length > 0 ||
    bodyChanged;

  return (
    <div className="space-y-3 min-w-0">
      <div className="text-[11px] muted-strong">
        Comparing <span className="mono">#{fromNo}</span> → <span className="mono">#{toNo}</span>
      </div>
      {!anyChange && <p className="text-xs muted italic">No differences detected.</p>}
      {scalarChanges.map((row) => (
        <div key={row.label} className="text-xs min-w-0">
          <div className="label mb-1">{row.label}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 min-w-0">
            <div className="rounded border border-danger/30 bg-danger/[0.06] px-2 py-1 mono text-[11px] break-all min-w-0">
              <span className="muted-strong mr-1">−</span>{row.before || <span className="italic muted">empty</span>}
            </div>
            <div className="rounded border border-emerald-500/30 bg-emerald-500/[0.06] px-2 py-1 mono text-[11px] break-all min-w-0 text-emerald-300">
              <span className="muted-strong mr-1">+</span>{row.after || <span className="italic muted">empty</span>}
            </div>
          </div>
        </div>
      ))}
      {headerChanges.length > 0 && (
        <KVDiffBlock label="Headers" changes={headerChanges} />
      )}
      {queryChanges.length > 0 && (
        <KVDiffBlock label="Query" changes={queryChanges} />
      )}
      {bodyChanged && (
        <div className="min-w-0">
          <div className="label mb-1">Body</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 min-w-0">
            <pre className="rounded border border-danger/30 bg-danger/[0.06] px-2 py-1.5 mono text-[11px] overflow-auto max-h-48 whitespace-pre-wrap break-all min-w-0">{a.body ?? ""}</pre>
            <pre className="rounded border border-emerald-500/30 bg-emerald-500/[0.06] px-2 py-1.5 mono text-[11px] overflow-auto max-h-48 whitespace-pre-wrap break-all min-w-0 text-emerald-300">{b.body ?? ""}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

// Per-line diff for headers/query lists. We pair entries by index; if
// either side has an entry at index i and they differ, that counts as
// changed (or added/removed if only one side has it). Good-enough for
// short kv lists — preserves order context the user has in their head.
type KVChange =
  | { kind: "added"; after: KV }
  | { kind: "removed"; before: KV }
  | { kind: "changed"; before: KV; after: KV };

function diffKVList(a: KV[], b: KV[]): KVChange[] {
  const out: KVChange[] = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    const x = a[i];
    const y = b[i];
    if (x && y) {
      if (x.key !== y.key || x.value !== y.value || (x.enabled !== false) !== (y.enabled !== false)) {
        out.push({ kind: "changed", before: x, after: y });
      }
    } else if (y) {
      out.push({ kind: "added", after: y });
    } else if (x) {
      out.push({ kind: "removed", before: x });
    }
  }
  return out;
}

function KVDiffBlock({ label, changes }: { label: string; changes: KVChange[] }) {
  return (
    <div className="min-w-0">
      <div className="label mb-1">{label}</div>
      <ul className="space-y-1">
        {changes.map((c, i) => (
          <li key={i} className="text-xs min-w-0">
            {c.kind === "added" && (
              <div className="rounded border border-emerald-500/30 bg-emerald-500/[0.06] px-2 py-1 mono text-[11px] break-all text-emerald-300">
                <span className="muted-strong mr-1">+</span>{c.after.key}: {c.after.value}
              </div>
            )}
            {c.kind === "removed" && (
              <div className="rounded border border-danger/30 bg-danger/[0.06] px-2 py-1 mono text-[11px] break-all">
                <span className="muted-strong mr-1">−</span>{c.before.key}: {c.before.value}
              </div>
            )}
            {c.kind === "changed" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 min-w-0">
                <div className="rounded border border-danger/30 bg-danger/[0.06] px-2 py-1 mono text-[11px] break-all min-w-0">
                  <span className="muted-strong mr-1">−</span>{c.before.key}: {c.before.value}
                </div>
                <div className="rounded border border-emerald-500/30 bg-emerald-500/[0.06] px-2 py-1 mono text-[11px] break-all min-w-0 text-emerald-300">
                  <span className="muted-strong mr-1">+</span>{c.after.key}: {c.after.value}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResponseViewer({
  response,
  error,
  pending,
  requestId,
  workspaceId,
}: {
  response: RunResult | null;
  error: string | null;
  pending: boolean;
  requestId: string;
  workspaceId: string;
}) {
  void requestId; void workspaceId;
  const [tab, setTab] = useState<"body" | "headers">("body");
  if (pending && !response) {
    return <div className="grid h-full place-items-center"><InlineLoading label="Sending request…" /></div>;
  }
  if (!response && !error) {
    return (
      <div className="grid h-full place-items-center muted text-sm">
        <div className="text-center">
          <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-surface-2 grid place-items-center text-text-faint">↩</div>
          <p>Press <span className="kbd">Send</span> to run this request.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="border-b border-border bg-surface px-3 py-2 flex items-center gap-3 flex-wrap">
        <StatusPill status={response?.status ?? null} error={response?.error ?? error} />
        {response && (
          <>
            <span className="text-xs muted-strong mono">{response.durationMs} ms</span>
            {response.size != null && (
              <span className="text-xs muted-strong mono">{formatBytes(response.size)}</span>
            )}
            {response.contentType && (
              <span className="text-xs muted-strong mono truncate">{response.contentType}</span>
            )}
          </>
        )}
        <div className="ml-auto flex gap-1">
          <TabBtn active={tab === "body"} onClick={() => setTab("body")}>Body</TabBtn>
          <TabBtn active={tab === "headers"} onClick={() => setTab("headers")}>Headers</TabBtn>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto scroll-pane">
        {response?.error || error ? (
          <div className="m-3 rounded-md border border-danger/40 bg-danger/10 p-3">
            <div className="text-xs uppercase tracking-wider muted-strong mb-1">Request failed</div>
            <pre className="response-body text-danger whitespace-pre-wrap">{prettyAgentError(response?.error ?? error ?? "")}</pre>
          </div>
        ) : tab === "body" ? (
          <div className="p-3"><ResponseBody body={response?.body ?? ""} contentType={response?.contentType ?? null} /></div>
        ) : (
          <div className="p-3"><HeaderList headers={response?.headers ?? null} /></div>
        )}
      </div>
    </div>
  );
}

// Make raw agent errors a little friendlier. We see strings like
// "agent_dispatch_failed:503" or "agent_offline" come back from the worker
// → translate to a one-line cause + hint.
function prettyAgentError(s: string): string {
  if (!s) return "";
  if (s.includes("agent_offline")) return "Agent is offline. Start your local rest-agent or pick a different executor.";
  if (s.startsWith("agent_dispatch_failed")) return "Couldn't reach the agent — connection dropped before the request returned.";
  if (s.includes("agent_timeout")) return "Agent didn't reply in time. Check the local terminal where rest-agent is running.";
  return s;
}

// Resolved-URL preview: shown below the URL bar when an environment is
// selected AND the URL actually contains {{var}} references. Highlights
// unresolved variables in red so it's obvious before you hit Send.
function ResolvedUrlPreview({ url, env }: { url: string; env: { vars: Record<string, string> } | undefined | null }) {
  if (!env) return null;
  const refs = findVars(url, env.vars);
  if (!refs.length) return null;
  const unresolved = refs.filter((r) => r.resolved === undefined);
  const resolved = interpolate(url, env.vars);
  return (
    <div className="px-3 py-1.5 border-b border-border bg-surface-2 text-xs flex items-center gap-2 overflow-x-auto">
      <span className="muted-strong shrink-0">resolves to</span>
      <code className="mono truncate">{resolved}</code>
      {unresolved.length > 0 && (
        <span className="chip bg-danger/15 text-danger shrink-0" title={`Missing: ${unresolved.map((u) => u.name).join(", ")}`}>
          {unresolved.length} unresolved
        </span>
      )}
    </div>
  );
}

type BodyView = "formatted" | "raw" | "preview";

function ResponseBody({ body, contentType }: { body: string; contentType: string | null }) {
  const lang = useMemo<SyntaxLang>(() => detectLangFromContentType(contentType), [contentType]);
  // Default: open the formatted view when we can pick a syntax for it,
  // otherwise drop straight to raw. Preview is the third option for
  // HTML content type so the user can see a rendered version.
  const supportsPreview = lang === "html";
  const [view, setView] = useState<BodyView>(lang === "plain" ? "raw" : "formatted");

  // Zero-byte bodies (204 No Content, HEAD requests, plain empty POSTs)
  // shouldn't render as a stark white pre — show a friendly placeholder.
  if (!body || body.length === 0) {
    return (
      <div className="empty-card">No body returned (0 bytes).</div>
    );
  }

  // Pretty-print only for JSON. Other syntaxes are usually already
  // well-formatted from the server.
  const formatted = useMemo(() => {
    if (lang === "json") return tryPrettyJson(body);
    return body;
  }, [body, lang]);

  return (
    <div className="flex flex-col gap-2 h-full min-h-0">
      <div className="flex items-center gap-1 flex-wrap">
        <ViewTab active={view === "formatted"} onClick={() => setView("formatted")} disabled={lang === "plain"}>
          Formatted
        </ViewTab>
        <ViewTab active={view === "raw"} onClick={() => setView("raw")}>Raw</ViewTab>
        {supportsPreview && (
          <ViewTab active={view === "preview"} onClick={() => setView("preview")}>Preview</ViewTab>
        )}
        <span className="ml-auto text-[11px] muted-strong mono">{lang === "plain" ? "text" : lang}</span>
      </div>
      <div className="flex-1 min-h-0 min-w-0">
        {view === "formatted" && lang !== "plain" && (
          <CodeViewer value={formatted} lang={lang} maxHeight="100%" />
        )}
        {(view === "raw" || lang === "plain") && (
          <pre className="response-body whitespace-pre-wrap break-words">{body}</pre>
        )}
        {view === "preview" && supportsPreview && (
          <HtmlPreview html={body} />
        )}
      </div>
    </div>
  );
}

function ViewTab({ active, onClick, disabled, children }: { active: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "px-2 py-0.5 rounded-sm text-xs font-medium transition " +
        (active
          ? "bg-accent text-[var(--ink-on-accent)]"
          : disabled
            ? "bg-surface-2 text-text-faint cursor-not-allowed"
            : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
      }
    >{children}</button>
  );
}

// Render server-returned HTML in a sandboxed iframe so its scripts can't
// touch our window. allow-same-origin is intentionally OFF — we want
// scripts to fail rather than have access to the parent document.
function HtmlPreview({ html }: { html: string }) {
  return (
    <iframe
      title="Response preview"
      sandbox=""
      srcDoc={html}
      className="w-full h-full min-h-[240px] rounded border border-border bg-white"
    />
  );
}

function TestsList({ tests }: { tests: TestResult[] }) {
  if (!tests.length) return <p className="muted text-sm">No tests ran.</p>;
  return (
    <ul className="space-y-1">
      {tests.map((t, i) => (
        <li key={i} className="flex items-start gap-3 rounded-md border border-border bg-surface-2 p-2.5 text-sm">
          <span className={"chip mt-0.5 " + (t.status === "pass" ? "bg-good/15 text-good" : t.status === "fail" ? "bg-danger/15 text-danger" : "bg-surface-3 muted-strong")}>{t.status}</span>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{t.name}</div>
            {t.message && <div className="text-xs muted-strong break-words">{t.message}</div>}
          </div>
          <span className="text-xs muted-strong mono">{t.durationMs}ms</span>
        </li>
      ))}
    </ul>
  );
}

function HeaderList({ headers }: { headers: Record<string, string> | null }) {
  if (!headers) return <p className="muted text-sm">No headers.</p>;
  const entries = Object.entries(headers);
  if (entries.length === 0) return <p className="muted text-sm">No headers.</p>;
  return (
    <ul className="text-xs mono space-y-1">
      {entries.map(([k, v]) => (
        <li key={k} className="grid grid-cols-[max-content_1fr] gap-3">
          <span className="text-text-dim">{k}:</span>
          <span className="break-all">{v}</span>
        </li>
      ))}
    </ul>
  );
}

function StatusPill({ status, error }: { status: number | null; error: string | null }) {
  if (error) return <span className="status-pill serr">ERROR</span>;
  if (status == null) return <span className="status-pill serr">—</span>;
  const band = status >= 500 ? "s5xx" : status >= 400 ? "s4xx" : status >= 300 ? "s3xx" : "s2xx";
  return <span className={`status-pill ${band}`}>{status}</span>;
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-2 py-1 text-xs font-medium rounded " +
        (active ? "bg-surface-3 text-text" : "text-text-dim hover:bg-surface-2 hover:text-text")
      }
    >{children}</button>
  );
}

function RequestMenu({ onDelete }: { onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
        aria-label="Request menu"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="12" r="1.5" fill="currentColor"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/><circle cx="19" cy="12" r="1.5" fill="currentColor"/></svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-48" role="menu">
          <button
            className="w-full text-left px-2 py-1 rounded hover:bg-surface-2 text-danger"
            onMouseDown={(e) => { e.preventDefault(); onDelete(); setOpen(false); }}
          >Delete</button>
        </div>
      )}
    </div>
  );
}

// Primary Send + dropdown rendered as ONE button visually: a single
// background, single border, single border-radius. Inside it, two
// transparent click regions share the surface — hovering either half
// brightens the whole pill, so it never feels like two adjacent buttons.
// The hairline divider between them is a subtle inner shadow, only visible
// as a faint groove like macOS split buttons.
function SendButton({ pending, disabled, disabledReason = null, onSend, onCopyCurl, onShowCode, className = "" }: {
  pending: boolean;
  disabled: boolean;
  /** One-line explanation surfaced as a tooltip when the button is
   *  disabled. Null means just visually-disabled with no extra hint. */
  disabledReason?: string | null;
  onSend: () => void;
  onCopyCurl: () => void;
  onShowCode: () => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [hoverTip, setHoverTip] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const inert = disabled || pending;
  // When the button is disabled (no URL etc.), the chevron should be
  // dead too — opening a "Copy as curl" menu on a request you can't run
  // sends a confusing message. We keep them in lockstep here.
  const auxDisabled = inert;
  return (
    <div
      ref={wrap}
      className={
        "split-btn relative inline-flex select-none " +
        (inert ? "is-disabled " : "") +
        className
      }
      onMouseEnter={() => inert && disabledReason && setHoverTip(true)}
      onMouseLeave={() => setHoverTip(false)}
      onFocus={() => inert && disabledReason && setHoverTip(true)}
      onBlur={() => setHoverTip(false)}
    >
      <button
        type="button"
        onClick={onSend}
        disabled={inert}
        aria-busy={pending || undefined}
        aria-describedby={inert && disabledReason ? "send-disabled-reason" : undefined}
        className="split-btn-main flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-semibold"
      >
        {pending && <Spinner />}
        <span>{pending ? "Sending…" : "Send"}</span>
      </button>
      <button
        type="button"
        aria-label={auxDisabled ? (disabledReason ?? "Send options") : "Send options"}
        disabled={auxDisabled}
        onClick={() => { if (auxDisabled) return; setOpen((v) => !v); }}
        className="split-btn-aux flex items-center justify-center px-2.5 py-1.5"
      >
        <ChevronDownIcon />
      </button>
      {open && !auxDisabled && (
        <div role="menu" className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-56">
          <MenuItem onSelect={() => { setOpen(false); onCopyCurl(); }}>Copy as curl</MenuItem>
          <MenuItem onSelect={() => { setOpen(false); onShowCode(); }}>Show in code…</MenuItem>
        </div>
      )}
      {hoverTip && inert && disabledReason && (
        <div
          id="send-disabled-reason"
          role="tooltip"
          className="send-disabled-tip absolute right-0 top-full mt-2 z-50"
        >
          {disabledReason}
        </div>
      )}
    </div>
  );
}

function MenuItem({ onSelect, children }: { onSelect: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="w-full text-left px-2 py-1.5 rounded hover:bg-surface-2 text-text"
      onMouseDown={(e) => { e.preventDefault(); onSelect(); }}
    >{children}</button>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
